import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  PartyPopper,
  Plus,
  ChevronLeft,
  ChevronRight,
  KanbanSquare,
  CalendarClock,
  Users,
  Eye,
} from 'lucide-react';
import { format, isPast, differenceInDays } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import type {
  EventItem,
  EventItemListParams,
  EventTag,
  EventTeamMember,
  EventWithKanbanSummary,
} from '../../../types/eventTeam';
import { EVENT_ITEM_STATUSES } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventStatusBadge } from '../EventStatusBadge';
import { EventPriorityBadge } from '../EventPriorityBadge';
import { EventFilters, EMPTY_EVENT_FILTERS, type EventFiltersState } from '../EventFilters';
import { EventItemModal } from '../EventItemModal';
import { useDebounce } from '../../../hooks/useDebounce';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

const PAGE_SIZE = 50;

export interface EventsListPanelProps {
  perms: EventTeamPerms;
  initialTagId?: string;
  onOpenEvent: (id: string) => void;
}

function localInputToMs(value: string): number | undefined {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

function dueDateStyle(dueDate: number, status: string): string {
  if (status === 'completed' || status === 'cancelled') return 'text-glass-400';
  const d = new Date(dueDate);
  if (isPast(d)) return 'text-red-400';
  if (differenceInDays(d, new Date()) <= 3) return 'text-amber-400';
  return 'text-glass-400';
}

/**
 * Events List / Pipeline (guide §1.3): full filter set on `GET /events/items`,
 * plus the kanban-augmented source (`GET /events-with-kanban-summary` — top-level
 * path, status filter + pagination only).
 */
export const EventsListPanel = ({ perms, initialTagId, onOpenEvent }: EventsListPanelProps) => {
  const [filters, setFilters] = useState<EventFiltersState>({
    ...EMPTY_EVENT_FILTERS,
    tagId: initialTagId || '',
  });
  const debouncedSearch = useDebounce(filters.search, 300);

  /** Workspace-counts view toggle (kanban-augmented list). */
  const [withKanbanCounts, setWithKanbanCounts] = useState(false);
  /** Full-text mode via the dedicated `GET /events/items/search?q=` endpoint (guide §1.3). */
  const [deepSearch, setDeepSearch] = useState(false);

  const [events, setEvents] = useState<(EventItem | EventWithKanbanSummary)[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tags, setTags] = useState<EventTag[]>([]);
  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  /** Overall count from the dedicated `GET /events/items/count` endpoint. */
  const [overallCount, setOverallCount] = useState<number | null>(null);

  useEffect(() => {
    eventTeamApiService.listTags().then(setTags).catch(() => setTags([]));
    eventTeamApiService
      .listTeamMembers({ limit: 200 })
      .then((res) => setMembers(res.results))
      .catch(() => setMembers([]));
    eventTeamApiService
      .countEvents()
      .then((res) => setOverallCount(res.count))
      .catch(() => setOverallCount(null));
  }, []);

  // Sync the tag filter when arriving from a dashboard chip.
  useEffect(() => {
    if (initialTagId) setFilters((prev) => ({ ...prev, tagId: initialTagId }));
  }, [initialTagId]);

  const queryParams = useMemo((): EventItemListParams => {
    return {
      status: filters.status || undefined,
      category: filters.category || undefined,
      event_type: filters.eventType || undefined,
      assignee: filters.assignee || undefined,
      tag_id: filters.tagId || undefined,
      search: debouncedSearch || undefined,
      priority_min: filters.priorityMin || undefined,
      priority_max: filters.priorityMax || undefined,
      scheduled_after: localInputToMs(filters.scheduledAfter),
      scheduled_before: localInputToMs(filters.scheduledBefore),
      due_after: localInputToMs(filters.dueAfter),
      due_before: localInputToMs(filters.dueBefore),
      sort_by: (filters.sortBy || 'created_at') as EventItemListParams['sort_by'],
      sort_order: filters.sortOrder,
    };
  }, [filters, debouncedSearch]);

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        if (withKanbanCounts) {
          const res = await eventTeamApiService.getEventsWithKanbanSummary({
            status: filters.status || undefined,
            page: targetPage,
            limit: PAGE_SIZE,
          });
          setEvents(res.results);
          setTotal(res.total);
        } else if (deepSearch && debouncedSearch.trim()) {
          // Dedicated full-text endpoint (guide §1.3).
          const res = await eventTeamApiService.searchEvents(debouncedSearch.trim(), targetPage, PAGE_SIZE);
          setEvents(res);
          setTotal(res.length);
        } else {
          const res = await eventTeamApiService.listEvents({
            ...queryParams,
            page: targetPage,
            limit: PAGE_SIZE,
          });
          setEvents(res.results);
          setTotal(res.total);
        }
        setPage(targetPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load events');
      } finally {
        setLoading(false);
      }
    },
    [queryParams, withKanbanCounts, deepSearch, debouncedSearch, filters.status],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Events"
        icon={PartyPopper}
        count={overallCount != null && overallCount !== total ? `${total} of ${overallCount}` : total}
        className="mb-0 flex-wrap"
        actions={
          <>
            <button
              type="button"
              onClick={() => setDeepSearch(!deepSearch)}
              className={cn(
                'glass-button-secondary btn-xs',
                deepSearch && '!text-accent-300 !border-accent-500/30 !bg-accent-500/10',
              )}
              aria-pressed={deepSearch}
              title="Use the dedicated full-text search endpoint instead of list filtering"
            >
              Deep search
            </button>
            <button
              type="button"
              onClick={() => setWithKanbanCounts(!withKanbanCounts)}
              className={cn(
                'glass-button-secondary btn-xs',
                withKanbanCounts && '!text-accent-300 !border-accent-500/30 !bg-accent-500/10',
              )}
              aria-pressed={withKanbanCounts}
              title="Augment the list with kanban card / assignee / watcher counts (limited filters)"
            >
              <KanbanSquare className="w-3.5 h-3.5" />
              Workspace counts
            </button>
            {perms.canCreate && (
              <button type="button" onClick={() => setShowCreate(true)} className="btn-primary">
                <Plus className="w-4 h-4" />
                New Event
              </button>
            )}
          </>
        }
      />

      <EventFilters
        variant="events"
        filters={filters}
        onChange={setFilters}
        statuses={EVENT_ITEM_STATUSES}
        tags={tags}
        members={members}
        advancedEnabled={!withKanbanCounts}
      />
      {withKanbanCounts && (
        <p className="text-xs text-glass-500">
          Workspace-counts view supports the status filter and pagination only.
        </p>
      )}

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="glass-card p-3 md:p-4 space-y-2">
              <SkeletonLoader className="h-4 w-56 max-w-full" />
              <SkeletonLoader className="h-3 w-full max-w-md" />
              <div className="flex gap-2">
                <SkeletonLoader className="h-5 w-20 rounded-full" />
                <SkeletonLoader className="h-5 w-10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      ) : events.length === 0 ? (
        <EmptyState icon={PartyPopper} title="No events match the current filters." />
      ) : (
        <div className="space-y-2">
          {events.map((event) => {
            const summary = withKanbanCounts ? (event as EventWithKanbanSummary) : null;
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => onOpenEvent(event.id)}
                className="w-full glass-card glass-card-interactive p-3 md:p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-semibold text-white block truncate">{event.title}</span>
                    {event.description && (
                      <span className="text-xs text-glass-400 line-clamp-1">{event.description}</span>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-glass-500">
                      <EventStatusBadge status={event.status} type="event" />
                      <EventPriorityBadge priority={event.priority} />
                      {event.category && (
                        <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{event.category}</span>
                      )}
                      <span className="text-glass-500 capitalize">{event.event_type.replace(/_/g, ' ')}</span>
                      {event.scheduled_start && (
                        <span className="inline-flex items-center gap-1 text-cyan-300/80">
                          <CalendarClock className="w-3 h-3" />
                          {format(new Date(event.scheduled_start), 'MMM d, HH:mm')}
                        </span>
                      )}
                      {event.due_date && (
                        <span className={dueDateStyle(event.due_date, event.status)}>
                          Due {format(new Date(event.due_date), 'MMM d')}
                        </span>
                      )}
                      {typeof event.quest_files_count === 'number' && event.quest_files_count > 0 && (
                        <span className="badge bg-amber-500/15 text-amber-300 border-amber-500/25">
                          {event.quest_files_count} quest file{event.quest_files_count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>

                  {summary && (
                    <div className="flex items-center gap-3 flex-shrink-0 text-xs text-glass-400 tabular-nums">
                      <span className="inline-flex items-center gap-1" title="Kanban links / open cards / done cards">
                        <KanbanSquare className="w-3.5 h-3.5 text-accent-400" />
                        {summary.kanban_links_count} · {summary.kanban_open_cards_count} open ·{' '}
                        {summary.kanban_done_cards_count} done
                      </span>
                      <span className="inline-flex items-center gap-1" title="Assignees">
                        <Users className="w-3.5 h-3.5" />
                        {summary.assignees_count}
                      </span>
                      <span className="inline-flex items-center gap-1" title="Watchers">
                        <Eye className="w-3.5 h-3.5" />
                        {summary.watchers_count}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-icon"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-icon"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <EventItemModal
        isOpen={showCreate}
        mode="create"
        onClose={() => setShowCreate(false)}
        onSuccess={(created) => onOpenEvent(created.id)}
      />
    </div>
  );
};
